import { useState, useEffect } from "react";
import {
  FaUser,
  FaPhone,
  FaEnvelope,
  FaPaperPlane,
  FaRocket,
  FaUsers,
  FaLightbulb,
  FaCheckCircle,
} from "react-icons/fa";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import emailjs from "@emailjs/browser";

export default function Enquiry({
  enquiryType = null,
  downloadMode = false,
  onSuccess = () => {},
}) {
  useEffect(() => {
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    if (publicKey) {
      emailjs.init(publicKey);
    } else {
      console.warn("EmailJS public key not found in environment variables");
    }
  }, []);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Name is required.";
    if (!form.phone.trim()) newErrors.phone = "Phone number is required.";
    if (!form.email.trim()) newErrors.email = "Email is required.";
    else if (!/\S+@\S+\.\S+/.test(form.email))
      newErrors.email = "Email is invalid.";
    if (!/^[6-9]\d{9}$/.test(form.phone))
      newErrors.phone = "Enter a valid 10-digit phone number.";

    return newErrors;
  };

  const sendEmails = async (data) => {
    try {
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const userTemplateId = process.env.REACT_APP_EMAILJS_USER_TEMPLATE_ID;
      const adminTemplateId = process.env.REACT_APP_EMAILJS_ADMIN_TEMPLATE_ID;

      if (!serviceId || !userTemplateId || !adminTemplateId) {
        throw new Error(
          "EmailJS configuration missing. Please check environment variables.",
        );
      }

      const templateParams = {
        name: data.name,
        email: data.email,
        phone: data.phone,
        enquiryType: enquiryType || "General Enquiry",
      };

      await emailjs.send(serviceId, userTemplateId, templateParams);

      await emailjs.send(serviceId, adminTemplateId, templateParams);

      console.log("Both emails sent successfully");
      return { success: true };
    } catch (error) {
      console.error("EmailJS error:", error);
      return { success: false, error: error.message };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setShowError(false);
    setErrorMessage("");

    try {
      await addDoc(collection(db, "enquiries"), {
        name: form.name,
        phone: form.phone,
        email: form.email,
        enquiryType: enquiryType,
        createdAt: serverTimestamp(),
        status: "new",
      });

      const emailResult = await sendEmails(form);

      if (!emailResult.success) {
        setShowError(true);
        setErrorMessage(
          emailResult.error ||
            "Failed to send confirmation email. Please contact support.",
        );
        setIsSubmitting(false);
        return;
      }

      setForm({ name: "", phone: "", email: "" });
      setErrors({});
      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
        onSuccess();
      }, 3000);
    } catch (error) {
      console.error("Form submission error:", error);
      setShowError(true);
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="bg-gradient-to-br from-[#f9fafb] to-[#f0f4f8] overflow-hidden py-16 sm:py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-[#0b1b4d]">
            Start your learning journey
          </h2>

          <div className="mt-4 flex justify-center gap-2">
            <span className="h-1 w-20 rounded-full bg-indigo-600" />
            <span className="h-1 w-3 rounded-full bg-indigo-300" />
          </div>

          <p className="mt-4 sm:mt-6 text-sm sm:text-base text-gray-600 leading-relaxed px-4 sm:px-0">
            Share your details and our team will reach out to guide you with the
            right course or internship.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
          <div className="space-y-6 sm:space-y-8">
            <h3 className="text-2xl sm:text-2xl md:text-3xl font-bold text-[#0b1b4d]">
              Why start with Ekamdhyas?
            </h3>

            <div className="space-y-5 sm:space-y-6">
              <Feature icon={<FaRocket />} title="Accelerate Your Career">
                Get hands-on experience with real projects and internships.
              </Feature>

              <Feature icon={<FaUsers />} title="Expert Mentorship">
                Learn from industry professionals who guide you every step.
              </Feature>

              <Feature icon={<FaLightbulb />} title="Tailored Learning">
                Courses designed to match your goals and pace.
              </Feature>

              <Feature icon={<FaCheckCircle />} title="Proven Results">
                Focused learning paths that help students build confidence and
                clarity.
              </Feature>
            </div>

            <p className="text-xs sm:text-sm text-gray-500 italic">
              Ready to transform your skills? Fill out the form and let's get
              started!
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-white border border-gray-200 rounded-2xl sm:rounded-3xl p-6 sm:p-10 md:p-12 shadow-xl"
          >
            <div className="space-y-5 sm:space-y-6">
              <div className="relative">
                <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  name="name"
                  placeholder="Full name"
                  value={form.name}
                  onChange={handleChange}
                  className={`w-full rounded-xl border px-11 sm:px-12 py-3 sm:py-4 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-indigo-500 transition ${
                    errors.name ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs sm:text-sm mt-1">
                    {errors.name}
                  </p>
                )}
              </div>

              <div className="relative">
                <FaPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone number"
                  value={form.phone}
                  onChange={handleChange}
                  className={`w-full rounded-xl border px-11 sm:px-12 py-3 sm:py-4 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-indigo-500 transition ${
                    errors.phone ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.phone && (
                  <p className="text-red-500 text-xs sm:text-sm mt-1">
                    {errors.phone}
                  </p>
                )}
              </div>

              <div className="relative">
                <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="email"
                  name="email"
                  placeholder="Email address"
                  value={form.email}
                  onChange={handleChange}
                  className={`w-full rounded-xl border px-11 sm:px-12 py-3 sm:py-4 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-indigo-500 transition ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs sm:text-sm mt-1">
                    {errors.email}
                  </p>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`mt-6 sm:mt-8 w-full py-3 sm:py-4 rounded-xl font-semibold text-white text-sm sm:text-base flex items-center justify-center gap-2 transition ${
                isSubmitting
                  ? "bg-gray-400"
                  : "bg-indigo-600 hover:bg-indigo-700"
              }`}
            >
              {isSubmitting ? "Submitting..." : "Submit enquiry"}{" "}
              <FaPaperPlane />
            </button>

            <p className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-500 text-center">
              You'll receive a confirmation email after submitting the form.
            </p>
          </form>
        </div>
      </div>
      {showSuccess && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50">
          <div className="bg-white border border-green-200 shadow-xl rounded-xl px-6 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <FaCheckCircle className="text-green-600 text-xl" />
            </div>

            <div>
              <p className="font-semibold text-gray-900">
                {downloadMode
                  ? "Your syllabus is downloading"
                  : "Enquiry submitted successfully"}
              </p>

              <p className="text-sm text-gray-600">
                {downloadMode
                  ? "Please check your downloads folder."
                  : "Our team will contact you shortly."}
              </p>
            </div>
          </div>
        </div>
      )}
      {showError && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50">
          <div className="bg-white border border-red-200 shadow-xl rounded-xl px-6 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <span className="text-red-600 text-xl">✕</span>
            </div>

            <div>
              <p className="font-semibold text-gray-900">Submission failed</p>
              <p className="text-sm text-gray-600">{errorMessage}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function Feature({ icon, title, children }) {
  return (
    <div className="flex items-start gap-3 sm:gap-4">
      <span className="text-indigo-600 text-lg sm:text-xl mt-1 flex-shrink-0">
        {icon}
      </span>
      <div>
        <h4 className="font-semibold text-sm sm:text-base text-gray-900">
          {title}
        </h4>
        <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
          {children}
        </p>
      </div>
    </div>
  );
}
